import React, { useState, useCallback } from 'react';
import { useInView } from 'react-intersection-observer';
import { twMerge } from 'tailwind-merge';
import useBlurhash from '../hooks/useBlurhash';
import skeletonStyles from '../styles/loadingSkeleton.module.scss';

type Props = React.DetailedHTMLProps<
  React.ImgHTMLAttributes<HTMLImageElement>,
  HTMLImageElement
> & { id: string; alt: string; blurhash?: string | null };

// Credit: https://gist.github.com/ngbrown/d62eb518753378eb0a9bf02bb4723235

// Uses browser-native `loading="lazy"` to lazy load images
// Renders a blurhash value to a blob when it about to appear on screen.
// Only renders the blurhash when the image hasn't loaded yet.
// Removes the blob once the image has finished loading.
export function BlurImg(allProps: Props) {
  const { loading = 'lazy', blurhash, alt, style, ...props } = allProps;

  const [imgLoaded, setImgLoaded] = useState(false);
  const [ref, inView] = useInView({ rootMargin: '110%' });
  const blurUrl = useBlurhash(!imgLoaded && inView ? blurhash : null);

  const handleOnLoad = useCallback(() => {
    setImgLoaded(true);
  }, []);

  const newStyle = blurUrl
    ? {
        ...style,
        backgroundImage: `url("${blurUrl}")`,
        backgroundSize:
          props.width && props.height
            ? `${props.width}px ${props.height}px`
            : '100% 100%',
      }
    : style;

  return (
    <div>
      <img
        ref={ref}
        alt={alt}
        {...props}
        loading={loading}
        onLoad={handleOnLoad}
        style={newStyle}
      />
      {!imgLoaded && !blurhash && (
        <div
          id={`skeleton-${allProps.id}`}
          className={twMerge('', skeletonStyles['loading-skeleton'])}
        />
      )}
    </div>
  );
}
